import './styles.scss'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { supabase } from 'Services/supabaseClient'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Checkbox } from 'primereact/checkbox'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import Loader from 'Components/Loader'
import LayoutContainer from 'Components/LayoutComponent'

const EditInternalCommunities = ({ setLoading }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const originalDataRef = useRef(null)
  const communityData = location?.state?.communityData

  const subscriptionOptions = ['R', 'G', 'P']

  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [showErrorDialog, setShowErrorDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {} })
  const [editFormFields, setEditFormFields] = useState({
    communityName: '',
    description: '',
    communityUrl: '',
    subscription: []
  })

  const [inputValidity, setInputValidity] = useState({
    communityName: false,
    subscription: false
  })

  useEffect(() => {
    if (communityData) {
      const originalValues = {
        communityName: communityData.communityName || '',
        description: communityData.description || '',
        communityUrl: communityData.communityUrl || '',
        subscription: communityData.communitySubscription || []
      }

      setEditFormFields(originalValues)
      originalDataRef.current = originalValues
    }
  }, [communityData])

  useEffect(() => {
    const changed = isFormChanged(editFormFields, originalDataRef.current)

    setIsButtonDisabled(!changed)
  }, [editFormFields])

  const updateInternalCommunity = async () => {
    setShowErrorDialog({ ...showErrorDialog, showModal: false })
    setLoading(true)

    try {
      const { communityName, communityUrl, description, subscription } = editFormFields

      const updateObject = {
        name: communityName,
        community_url: communityUrl,
        description: description,
        required_subscription: subscription
      }

      const { data, error, status } = await supabase.from('communities').update(updateObject).eq('id', communityData.communityId).select('id').single()

      if (error) {
        setShowErrorDialog({
          ...showErrorDialog,
          showModal: true,
          modalType: 'error',
          message: t('FAILED_TO_UPDATE_COMMUNITY'),
          onRetry: () => updateInternalCommunity(),
          onHide: handleDismiss
        })
      }

      if (status === 200 && data?.id) {
        setShowErrorDialog({
          ...showErrorDialog,
          showModal: true,
          modalType: 'success',
          message: t('COMMUNITY_UPDATED_SUCCESSFULLY'),
          onHide: handleDismiss,
          onSuccess: handleSuccess
        })
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const isFormChanged = (current, original) => {
    if (!original) return false

    return current.communityName !== original.communityName || current.communityUrl !== original.communityUrl || current.description !== original.description || JSON.stringify(current.subscription) !== JSON.stringify(original.subscription)
  }

  const handleSubmit = () => {
    const isCommunityNameValid = !!editFormFields.communityName
    const isSubscriptionValid = editFormFields.subscription && editFormFields.subscription.length > 0

    setInputValidity({
      communityName: true,
      subscription: true
    })

    if (isCommunityNameValid && isSubscriptionValid) {
      updateInternalCommunity()
    }
  }

  const handleInputChange = (field, value) => {
    setIsButtonDisabled(false)

    setEditFormFields((prevState) => ({
      ...prevState,
      [field]: value
    }))
  }

  const handleCheckboxChange = (value) => {
    setIsButtonDisabled(false)
    const index = subscriptionOptions.indexOf(value)

    setEditFormFields((prev) => {
      let updatedValues = prev.subscription || []

      if (updatedValues.includes(value)) {
        updatedValues = updatedValues.filter((item) => subscriptionOptions.indexOf(item) < index)
      } else {
        updatedValues = [...new Set([...updatedValues, ...subscriptionOptions.slice(index)])]
      }

      setInputValidity((prev) => ({
        ...prev,
        subscription: true
      }))

      return {
        ...prev,
        subscription: updatedValues
      }
    })
  }

  const handleBlur = (fieldName) => {
    setInputValidity((prev) => ({
      ...prev,
      [fieldName]: true
    }))
  }

  const handleDismiss = () => {
    setShowErrorDialog({ ...showErrorDialog, showModal: false })
  }

  const handleSuccess = () => {
    history.push(ROUTE_STRINGS.internalCommunitiesManagement)
  }

  return (
    <LayoutContainer title={t('EDIT_INTERNAL_COMMUNITIES')} breadCrumbsTitle={'editInternalCommunities'}>
      <div className="edit-internal-communities-wrapper">
        <div className="form-fields-row">
          <div className="form-field">
            <span className="p-float-label">
              <InputText id="communityName" name="communityName" value={editFormFields.communityName} className={`input-control ${inputValidity.communityName && !editFormFields.communityName ? 'error' : ''}`} onChange={(event) => handleInputChange('communityName', event.target.value)} onBlur={() => handleBlur('communityName')} />
              <label htmlFor="communityName" className={`input-label ${inputValidity.communityName && !editFormFields.communityName ? 'error' : ''}`}>
                {t('COMMUNITY_NAME')} <span className="required">*</span>
              </label>
            </span>
            {inputValidity.communityName && !editFormFields.communityName && <small className="error-message">{t('COMMUNITY_NAME_ERROR_MESSAGE')}</small>}
          </div>

          <div className="form-field">
            <span className="p-float-label">
              <InputText id="communityUrl" name="communityUrl" value={editFormFields.communityUrl} className={'input-control'} onChange={(event) => handleInputChange('communityUrl', event.target.value)} />
              <label htmlFor="communityUrl">{t('COMMUNITY_URL')}</label>
            </span>
          </div>
        </div>

        <div className="form-fields-row">
          <div className="form-field">
            <span className="p-float-label">
              <InputTextarea rows={5} id="description" name="description" value={editFormFields.description} className={'input-control'} onChange={(event) => handleInputChange('description', event.target.value)} />
              <label htmlFor="description">{t('COMMUNITY_DESCRIPTION')}</label>
            </span>
          </div>

          <div className="checkbox-wrapper">
            <label className={`section-label ${inputValidity.subscription && (!editFormFields.subscription || editFormFields.subscription.length === 0) ? 'error' : ''}`}>
              {t('SUBSCRIPTION_TYPE')} <span className="required">*</span>
            </label>
            <div className="checkbox-group">
              <div className="checkbox-option">
                <Checkbox inputId="regular" name="subscription" value="R" onChange={(e) => handleCheckboxChange(e.value)} checked={editFormFields.subscription?.includes('R')} />
                <label htmlFor="regular">{t('REGULAR_SUBSCRIPTION')}</label>
              </div>
              <div className="checkbox-option">
                <Checkbox inputId="gold" name="subscription" value="G" onChange={(e) => handleCheckboxChange(e.value)} checked={editFormFields.subscription?.includes('G')} />
                <label htmlFor="gold">{t('GOLD_SUBSCRIPTION')}</label>
              </div>
              <div className="checkbox-option">
                <Checkbox inputId="platinum" name="subscription" value="P" onChange={(e) => handleCheckboxChange(e.value)} checked={editFormFields.subscription?.includes('P')} />
                <label htmlFor="platinum">{t('PLATINUM_SUBSCRIPTION')}</label>
              </div>
            </div>
            {inputValidity.subscription && (!editFormFields?.subscription || editFormFields?.subscription?.length === 0) && <small className="error-message">{t('SUBSCRIPTION_TYPE_IS_REQUIRED')}</small>}
          </div>
        </div>

        <div className="community-form-actions">
          <ButtonComponent type={'cancel'} onClick={() => history.push(ROUTE_STRINGS.internalCommunitiesManagement)} />
          <ButtonComponent type="submit" onClick={handleSubmit} disabled={!editFormFields.communityName || !editFormFields.subscription || editFormFields.subscription.length === 0 || isButtonDisabled} />
        </div>
      </div>
      <DialogModal {...showErrorDialog} onDismiss={handleDismiss} />
    </LayoutContainer>
  )
}

export default Loader(EditInternalCommunities)
